// 079: where a visitor lands after signing in.
//
// One resolver answers that question for all three post-sign-in navigations
// (LoginPage's effect, LoginPage's submit handler, AuthCallbackPage's Google exchange).
// LoginPage's two arms race — the effect's dependency flips inside login(), before the
// submit handler's own navigation runs — and this module is why that stops mattering:
// both arms ask the same question and get the same answer (plan D3).
//
// The stored value is untrusted input whatever wrote it, so it is validated on READ.

/** The single default landing destination (FR-006). Exported so tests assert against it
 *  rather than a copied literal. */
export const DEFAULT_DESTINATION = '/';

const STORAGE_KEY = 'carii.signin_return_path';

/** Remember where the visitor was going. Called ONLY when a protected destination is
 *  refused because they are not signed in — never from the "back to sign in" links or
 *  the terminal-failure redirects, which would capture the sign-in journey and loop
 *  (FR-009, research R1). No validation on write: validation is on read, so a value
 *  that arrives by any route is still checked. */
export function rememberReturnPath(path: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // The store can refuse a write (quota, or a browser blocking storage entirely). The
    // visitor simply lands on the default afterwards — FR-012 forbids saying anything, and
    // an exception here would surface on the way to the sign-in screen.
  }
}

/** V1 — a destination is a path within this application: it begins with exactly one `/`.
 *  Refuses `https://evil.example`, `javascript:…` and a bare host. `//evil.example` is
 *  the standard bypass of a naive leading-slash test, so it is refused here too — but V2
 *  is what makes that structural rather than a spelling. */
function isSameOriginPath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//');
}

/** V2 — resolving the value against the current origin must leave the origin unchanged.
 *  This is not decoration behind V1: `/\evil.example` begins with exactly one forward
 *  slash, so V1 admits it, and the URL parser resolves it to http://evil.example. V2 is
 *  the check that makes "stays on this site" structural rather than a spelling test. */
function staysOnThisOrigin(value: string): boolean {
  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    // An unparseable value is refused, silently — FR-012 forbids telling the visitor.
    return false;
  }
}

/** The sign-in journey itself. Being returned to any of these loops the visitor back
 *  into the flow they just completed (FR-008).
 *
 *  🔴 This is NOT main.tsx's `showNav` exclusion list, though six of seven coincide, and
 *  the two must not be shared. They differ in both directions: `/account/security` is
 *  showNav-excluded yet is a legitimate destination here, and `/impersonate` is not
 *  showNav-excluded yet must be refused here. One list would let a change to the page
 *  chrome silently alter redirect safety (plan D5).
 *
 *  There is deliberately no allowlist of the 11 protected destinations: FR-010 forbids
 *  special-casing, and such a list would go stale on the twelfth — silently, by sending a
 *  legitimate visitor to the default. */
const SIGN_IN_JOURNEY_PREFIXES = [
  '/account/login',
  '/account/register',
  '/account/forgot-password',
  '/account/reset-password',
  '/account/verify-email',
  '/auth/callback',
  '/impersonate',
];

/** V3. Matched on whole path segments, so `/account/logins-report` is not caught by the
 *  `/account/login` prefix — a plain startsWith would refuse a legitimate destination. */
function isSignInJourney(value: string): boolean {
  const path = value.split(/[?#]/)[0] ?? value;
  return SIGN_IN_JOURNEY_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Read the stored destination, tolerating a store that cannot be read at all. Returns
 *  null rather than throwing: a value this module cannot obtain is indistinguishable, to
 *  the visitor, from there being no value. */
function readStoredPath(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearStoredPath(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do and nothing to say — the validation on read is what protects arrival.
  }
}

/** The credential that identifies the current sign-in. Read defensively: this is also the
 *  key node 25 shadows on `localStorage`, and a store that refuses reads must not throw
 *  out of the resolver. */
function readAccessToken(): string | null {
  try {
    return localStorage.getItem('access_token');
  } catch {
    return null;
  }
}

function validate(stored: string): string {
  if (!isSameOriginPath(stored)) return DEFAULT_DESTINATION;
  if (!staysOnThisOrigin(stored)) return DEFAULT_DESTINATION;
  if (isSignInJourney(stored)) return DEFAULT_DESTINATION;
  return stored;
}

/** 🔴 The answer already given during THIS sign-in, keyed by the credential that identifies
 *  it. Both of LoginPage's arms resolve for one sign-in, and consume-and-return naively
 *  would give them destination-then-default — whichever landed last would win with the
 *  default and the race would be back by the back door. The memo is what makes the arms
 *  unable to disagree.
 *
 *  It is keyed on the access token rather than merely held in module scope because
 *  `logout()` does NOT reload the document (verified in AuthContext) — an unkeyed memo would
 *  hand a later sign-in in the same tab a stale destination, and FR-004 says a sign-in that
 *  followed no refusal lands on the default. A new sign-in mints a new token, so the memo
 *  stops matching and the default is returned.
 *
 *  🔴 The token is NOT optional for a memo hit. An absent token cannot identify a sign-in, so
 *  two unrelated sign-ins would both match `null` and share one answer — the same staleness
 *  this key exists to prevent. When there is no token the memo is neither written nor read,
 *  and the store alone decides. */
let resolvedThisSignIn: { token: string; destination: string } | null = null;

/** Where a just-signed-in visitor should be sent. Reads, validates and CONSUMES any
 *  remembered destination; returns the default when there is none or it is refused. Every
 *  refusal is silent (FR-012), and the value is never logged — it is untrusted input.
 *
 *  Idempotent within one sign-in: calling twice returns the same answer. */
export function resolvePostSignInDestination(): string {
  const token = readAccessToken();
  const stored = readStoredPath();

  // `!== null` rather than a truthiness test: an empty stored value is still a value to be
  // consumed, and treating it as absent would leave it sitting in the store.
  if (stored !== null) {
    const destination = validate(stored);
    // Consumed inside the resolver so a caller cannot forget it (FR-004). The refused case
    // is consumed too: a value that failed validation must not sit waiting for a later
    // sign-in either.
    clearStoredPath();
    if (token !== null) resolvedThisSignIn = { token, destination };
    return destination;
  }

  if (token !== null && resolvedThisSignIn?.token === token) {
    return resolvedThisSignIn.destination;
  }

  return DEFAULT_DESTINATION;
}
